import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import type { RootState } from "@/store"
import type {
  Careers24ConnectionDto,
  Careers24UpsertRequestDto,
} from "../dtos/Careers24ConnectDto"
import {
  getConnection,
  upsertConnection,
  deleteConnection,
} from "../infrastructure/careers24ConnectorService"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"

function getErrorMessage(err: unknown, fallback: string) {
  if (typeof err === "object" && err !== null) {
    const maybeResponse = (err as { response?: unknown }).response
    if (typeof maybeResponse === "object" && maybeResponse !== null) {
      const maybeData = (maybeResponse as { data?: unknown }).data
      if (typeof maybeData === "object" && maybeData !== null) {
        const maybeMessage = (maybeData as { message?: unknown }).message
        if (typeof maybeMessage === "string") return maybeMessage
      }
    }
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

interface Careers24ConnectorState {
  connection: Careers24ConnectionDto | null
  isLoading: boolean
  isSubmitting: boolean
  isDeleting: boolean
  error: string | null
  isSuccess: boolean
}

const initialState: Careers24ConnectorState = {
  connection: null,
  isLoading: false,
  isSubmitting: false,
  isDeleting: false,
  error: null,
  isSuccess: false,
}

export const fetchCareers24ConnectionThunk = createAsyncThunk<
  Careers24ConnectionDto | null,
  void,
  { state: RootState; rejectValue: string }
>(
  "profile/careers24/fetchConnection",
  async (_, { getState, rejectWithValue }) => {
    try {
      const orgId = selectCurrentOrganizationId(getState())
      return await getConnection(orgId)
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to load Careers24 connection"),
      )
    }
  },
)

export const upsertCareers24ConnectionThunk = createAsyncThunk<
  Careers24ConnectionDto,
  Careers24UpsertRequestDto,
  { state: RootState; rejectValue: string }
>(
  "profile/careers24/upsertConnection",
  async (dto, { getState, rejectWithValue }) => {
    try {
      const orgId = selectCurrentOrganizationId(getState())
      const result = await upsertConnection(orgId, dto)
      if (result.success === false) {
        return rejectWithValue(
          result.message ?? "Careers24 connection validation failed",
        )
      }
      return dto
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to save Careers24 connection"),
      )
    }
  },
)

export const deleteCareers24ConnectionThunk = createAsyncThunk<
  void,
  void,
  { state: RootState; rejectValue: string }
>(
  "profile/careers24/deleteConnection",
  async (_, { getState, rejectWithValue }) => {
    try {
      const orgId = selectCurrentOrganizationId(getState())
      await deleteConnection(orgId)
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to delete Careers24 connection"),
      )
    }
  },
)

const careers24ConnectorSlice = createSlice({
  name: "profileCareers24Connector",
  initialState,
  reducers: {
    clearCareers24ConnectorError(state) {
      state.error = null
    },
    clearCareers24ConnectorState() {
      return initialState
    },
    resetCareers24Success(state) {
      state.isSuccess = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCareers24ConnectionThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCareers24ConnectionThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.connection = action.payload
      })
      .addCase(fetchCareers24ConnectionThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload ?? "Failed to load Careers24 connection"
      })

      .addCase(upsertCareers24ConnectionThunk.pending, (state) => {
        state.isSubmitting = true
        state.error = null
        state.isSuccess = false
      })
      .addCase(upsertCareers24ConnectionThunk.fulfilled, (state, action) => {
        state.isSubmitting = false
        state.connection = action.payload
        state.isSuccess = true
      })
      .addCase(upsertCareers24ConnectionThunk.rejected, (state, action) => {
        state.isSubmitting = false
        state.error = action.payload ?? "Failed to save Careers24 connection"
      })

      .addCase(deleteCareers24ConnectionThunk.pending, (state) => {
        state.isDeleting = true
        state.error = null
      })
      .addCase(deleteCareers24ConnectionThunk.fulfilled, (state) => {
        state.isDeleting = false
        state.connection = null
      })
      .addCase(deleteCareers24ConnectionThunk.rejected, (state, action) => {
        state.isDeleting = false
        state.error = action.payload ?? "Failed to delete Careers24 connection"
      })

      .addMatcher(
        (action) => action.type === "organizations/setCurrentOrganizationId",
        () => initialState,
      )
  },
})

export const {
  clearCareers24ConnectorError,
  clearCareers24ConnectorState,
  resetCareers24Success,
} = careers24ConnectorSlice.actions

export default careers24ConnectorSlice.reducer

export const selectCareers24Connection = (state: RootState) =>
  state.careers24Connector.connection
export const selectCareers24IsLoading = (state: RootState) =>
  state.careers24Connector.isLoading
export const selectCareers24IsSubmitting = (state: RootState) =>
  state.careers24Connector.isSubmitting
export const selectCareers24IsDeleting = (state: RootState) =>
  state.careers24Connector.isDeleting
export const selectCareers24Error = (state: RootState) =>
  state.careers24Connector.error
export const selectCareers24IsSuccess = (state: RootState) =>
  state.careers24Connector.isSuccess
